import {  useCallback, useRef } from "react";


interface PerformanceMetric {
  name: string;
  value: number;
  rating: "good" | "needs-improvement" | "poor";
  timestamp: number;
  id: string;
}

interface DeviceInfo {
  userAgent: string;
  viewport: { width: number; height: number };
  connection?: string;
  deviceMemory?: number;
  platform: string;
}

class PerformanceTracker {
  private static instance: PerformanceTracker;
  private metrics: PerformanceMetric[] = [];
  private sessionId: string;
  private deviceInfo: DeviceInfo;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.deviceInfo = this.getDeviceInfo();
  }

  static getInstance(): PerformanceTracker {
    if (!PerformanceTracker.instance) {
      PerformanceTracker.instance = new PerformanceTracker();
    }
    return PerformanceTracker.instance;
  }

  private generateSessionId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  private getDeviceInfo(): DeviceInfo {
    const nav = navigator as any;
    return {
      userAgent: navigator.userAgent,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight,
      },
      connection: nav.connection?.effectiveType || "unknown",
      deviceMemory: nav.deviceMemory || undefined,
      platform: navigator.platform,
    };
  }

  

  

  getPerformanceSummary() {
    const summary = {
      totalMetrics: this.metrics.length,
      goodMetrics: this.metrics.filter((m) => m.rating === "good").length,
      needsImprovementMetrics: this.metrics.filter(
        (m) => m.rating === "needs-improvement"
      ).length,
      poorMetrics: this.metrics.filter((m) => m.rating === "poor").length,
      averageByMetric: {} as Record<string, number>,
      deviceInfo: this.deviceInfo,
      sessionId: this.sessionId,
    };

    const metricTypes = [...new Set(this.metrics.map((m) => m.name))];
    metricTypes.forEach((type) => {
      const values = this.metrics
        .filter((m) => m.name === type)
        .map((m) => m.value);
      if (values.length > 0) {
        summary.averageByMetric[type] =
          values.reduce((a, b) => a + b, 0) / values.length;
      }
    });

    return summary;
  }

  getAllMetrics() {
    return [...this.metrics];
  }
}

const useWebVitals = (pageName: string = "Unknown Page") => {
  const tracker = useRef(PerformanceTracker.getInstance());


  

  const getPerformanceReport = useCallback(() => {
    const summary = tracker.current.getPerformanceSummary();
    const recommendations = [];

    if (summary.poorMetrics > 0) {
      recommendations.push("⚠️ هناك مقاييس أداء ضعيفة تحتاج تحسين");
    }

    if (summary.averageByMetric.LCP > 2500) {
      recommendations.push("🖼️ تحسين تحميل الصور والمحتوى الرئيسي");
    }

    if (summary.averageByMetric.FID > 100) {
      recommendations.push("⚡ تحسين استجابة التفاعل مع الصفحة");
    }

    if (summary.averageByMetric.CLS > 0.1) {
      recommendations.push("📐 تقليل حركة العناصر غير المتوقعة");
    }

    return {
      page: pageName,
      url: window.location.href,
      summary,
      recommendations,
      allMetrics: tracker.current.getAllMetrics(),
    };
  }, [pageName]);

  const logDetailedReport = useCallback(() => {
    if (process.env.NODE_ENV !== "development") return;

    const report = getPerformanceReport();

    console.group("📈 تقرير الأداء الشامل - " + report.page);
    console.log("📊 إجمالي المقاييس:", report.summary.totalMetrics);
    console.log("🟢 جيد:", report.summary.goodMetrics);
    console.log("🟡 يحتاج تحسين:", report.summary.needsImprovementMetrics);
    console.log("🔴 ضعيف:", report.summary.poorMetrics);
    console.log("📱 معلومات الجهاز:", report.summary.deviceInfo);
    if (report.recommendations.length > 0) {
      console.log("💡 التوصيات:", report.recommendations);
    }
    console.groupEnd();
  }, [getPerformanceReport]);

 
  return {
    getPerformanceReport,
    logDetailedReport,
    tracker: tracker.current,
  };
};

export default useWebVitals;
